import { useCallback, useEffect, useReducer, useState } from 'react'
import LoginScreen from './components/LoginScreen'
import { canRevealOtherPlayerGameweek } from './predictionCycle'

import { useThemePreference } from './app/useThemePreference'
import { useIdentity } from './app/useIdentity'
import { useSeasonArchive } from './app/useSeasonArchive'
import { useGameweekState, STARTING_GAMEWEEK } from './app/useGameweekState'
import { usePredictions } from './app/usePredictions'
import { useChips } from './app/useChips'
import { useQuestPicks, useHasStartedGw } from './app/useQuestPicks'
import { useRevealSeen } from './app/useRevealSeen'
import { useLeagues } from './app/useLeagues'
import { useLeaguesRefresh } from './app/useLeaguesRefresh'
import { useExploreLeagues } from './app/useExploreLeagues'
import { useLeagueJoinRequests } from './app/useLeagueJoinRequests'
import { useRivals } from './app/useRivals'
import { useViewedPlayer } from './app/useViewedPlayer'
import { useTopScorer } from './app/useTopScorer'
import { useGameweekStats } from './app/useGameweekStats'
import { useGameweekSimulation } from './app/useGameweekSimulation'
import { useDevTools } from './app/useDevTools'
import { useSeasonScores } from './app/useSeasonScores'
import { useAppliedPhase, useGameweekPhasePoll } from './app/useGameweekPhasePoll'
import { useSessionBootstrap } from './app/useSessionBootstrap'
import { useInstallPrompt } from './app/useInstallPrompt'
import { useUpdateNotice } from './app/useUpdateNotice'
import { useLivePoll } from './app/useLivePoll'
import { useForecasts } from './app/useForecasts'
import { onWorldChanged } from './app/worldRefresh'
import { useLeagueNavigation, useGameweekHistory } from './app/useScreenNavigation'
import { useOtherPlayerGameweekDetail } from './app/useOtherPlayerGameweekDetail'
import { areResultsVisible } from './reveal'
import AppRoutes from './app/AppRoutes'
import UpdateNoticeModal from './components/UpdateNoticeModal'
import { RulesProvider } from './features/rules/RulesProvider'
import type { AppView } from './app/AppView'
import type { League } from './types'
import './App.css'

/**
 * The root component is wiring only: it owns the load gate, calls each domain hook,
 * and hands the assembled view to `AppRoutes`. Every behaviour lives in `src/app/*`, each with its
 * own tests.
 *
 * Hook order is load bearing in one place: `useGameweekPhasePoll` and `useSessionBootstrap` come
 * last, because they write into the hooks above and read them back through a ref, which is what
 * lets both stay genuinely mount-once. (`useGameweekState` used to have to come first too, to
 * resolve the per-user kickoff anchor — retired that.)
 */
function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // null = still checking; false = show LoginScreen instead of the app (no anonymous/guest mode).
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const theme = useThemePreference()
  const season = useSeasonArchive()
  const leagueNav = useLeagueNavigation()
  const history = useGameweekHistory(season.seasonHistory, season.seasonDetails)
  // The fetched season store lives outside React, so a results refresh has to be
  // announced. `useReducer`'s dispatch is identity-stable, which is what lets the live poll below
  // take it as an effect dependency without restarting on every render.
  const [seasonVersion, bumpSeasonVersion] = useReducer((value: number) => value + 1, 0)
  // The dev rail's replica-clock controls move the whole world; the same
  // announcement, from a rail four components below this one. `bumpSeasonVersion` is a `useReducer`
  // dispatch, so the subscription is set up once and never torn down on a re-render.
  useEffect(() => onWorldChanged(bumpSeasonVersion), [])
  const gameweek = useGameweekState({ isLoading, onToast: setToastMessage, seasonVersion })
  const { currentGameweek, games } = gameweek
  const { hasStartedGw, markStarted } = useHasStartedGw(currentGameweek, isLoading)
  // Sits above `usePredictions` because a saved prediction is what arms it.
  const installPrompt = useInstallPrompt()
  const predictions = usePredictions({
    currentGameweek,
    games,
    isDeadlinePassed: gameweek.isDeadlinePassed,
    isLoading,
    onToast: setToastMessage,
    onStartGameweek: markStarted,
    onPredictionSaved: installPrompt.notePredictionSaved,
  })
  const chips = useChips({
    currentGameweek,
    isDeadlinePassed: gameweek.isDeadlinePassed,
    isLoading,
    onToast: setToastMessage,
    seasonDetails: season.seasonDetails,
    seasonHistory: season.seasonHistory,
    // A chip's "final" number is frozen at settlement, not at the first recomputation — scoring is
    // continuous now, so a stored detail exists long before the gameweek is over.
    settledGameweeks: gameweek.settledGameweeks,
  })
  const quests = useQuestPicks({
    currentGameweek,
    isLoading,
    seasonVersion,
    onToast: setToastMessage,
  })
  const revealSeen = useRevealSeen(currentGameweek, isLoading)

  const leagues = useLeagues({
    onToast: setToastMessage,
    onOpenLeague: leagueNav.open,
    onCloseLeague: leagueNav.close,
  })
  const leaguesRefresh = useLeaguesRefresh({
    setLeagues: leagues.setLeagues,
    readMutationToken: leagues.readMutationToken,
    onToast: setToastMessage,
  })
  // Both league-directory hooks patch the same list `useLeagues` owns, so they take a callback
  // rather than a second copy of it.
  const { setLeagues } = leagues
  const patchLeague = useCallback(
    (league: League) =>
      setLeagues((prev) =>
        prev.some((l) => l.id === league.id)
          ? prev.map((l) => (l.id === league.id ? league : l))
          : [...prev, league]
      ),
    [setLeagues]
  )
  const exploreLeagues = useExploreLeagues({
    onToast: setToastMessage,
    onJoined: patchLeague,
    onOpenLeague: leagueNav.open,
  })
  const openLeague = leagues.leagues.find((l) => l.id === leagueNav.selectedLeagueId)
  const joinRequests = useLeagueJoinRequests({
    leagueId: leagueNav.selectedLeagueId,
    // Only the owner is sent their own invite code, which is what makes it the ownership signal
    // the detail screen already uses.
    isOwner: Boolean(openLeague?.inviteCode),
    onToast: setToastMessage,
    onLeagueUpdated: patchLeague,
  })
  const identity = useIdentity({ onToast: setToastMessage, setLeagues: leagues.setLeagues })
  const rivals = useRivals({ myUserId: identity.myUserId, onToast: setToastMessage })
  // Live scores come from OUR server, polled independently of how often we poll the provider:
  // clients hitting us every 10s costs nothing, we hit the provider every 60s.
  //
  // Sits after `useIdentity` for `dataMode`: the poll's own "is a match plausibly
  // in play right now" gate reads the browser's clock, which in replica mode is not the clock the
  // dev rail moves, so it must not run there.
  const live = useLivePoll({
    ready: !isLoading && isSignedIn === true,
    kickoffKey: games.map((game) => game.kickoff).join(','),
    dataMode: identity.dataMode,
    onResultsRefreshed: bumpSeasonVersion,
  })
  // One request for the whole gameweek's pre-match forecasts, fired on the gameweek
  // changing and nothing else — opening a fixture in the picker must cost nothing.
  const forecasts = useForecasts(currentGameweek, !isLoading && isSignedIn === true)
  const simulation = useGameweekSimulation()
  const devTools = useDevTools({
    // Both halves, separately: who is asking, and which world we are in. Each
    // handler is gated on its own tool's capability rather than on one all-or-nothing boolean.
    isAdmin: identity.isAdmin,
    mode: identity.dataMode,
    currentGameweek,
    games,
    predictions: predictions.predictions,
    bankerGameId: predictions.bankerGameId,
    resultsRevealed: gameweek.resultsRevealed,
    simulation,
    setCurrentGameweek: gameweek.setCurrentGameweek,
    setPredictions: predictions.setPredictions,
    setBankerGameId: predictions.setBankerGameId,
    setResultsRevealed: gameweek.setResultsRevealed,
    markDeadlinePassed: gameweek.markDeadlinePassed,
    setSeasonHistory: season.setSeasonHistory,
    setSeasonDetails: season.setSeasonDetails,
    setLeagues: leagues.setLeagues,
    onToast: setToastMessage,
  })

  // A finished fixture reveals itself against real football, rather than waiting for the whole
  // gameweek's flag. In mock the flag is still the only answer — see `reveal.ts`.
  const resultsVisible = areResultsVisible(gameweek.resultsRevealed, identity.dataMode)

  const scores = useSeasonScores({
    currentGameweek,
    games,
    predictions: predictions.predictions,
    actualResults: gameweek.actualResults,
    bankerGameId: predictions.bankerGameId,
    resultsRevealed: gameweek.resultsRevealed,
    resultsVisible,
    isDoubleUpActive: chips.isDoubleUpActive,
    isCleanSweepActive: chips.isCleanSweepActive,
    isGoalfestActive: chips.isGoalfestActive,
    seasonHistory: season.seasonHistory,
    // Feeds `liveGameweekPoints` only — the season totals stay on the wholesale reveal.
    simGameStatuses: simulation.gameStatuses,
  })

  // Other-player "GW {N} Calls" reveal window: a gameweek's
  // calls are visible from its own deadline passing until the app advances past it.
  const otherPlayerRevealGameweek = canRevealOtherPlayerGameweek(
    currentGameweek,
    gameweek.deadlinePassedGameweeks.includes(currentGameweek)
  )
    ? currentGameweek
    : null
  const viewedPlayer = useViewedPlayer(otherPlayerRevealGameweek)
  // Who is top of this gameweek, opened over the Predict screen by the hero's Highest tile.
  const topScorer = useTopScorer()
  // The gameweek's real average and highest, for the two stats flanking the Predict hero. Keyed on
  // `seasonVersion`, so they move on the same signal as the user's own total and the league tables.
  // Not asked for before the deadline: the hero card is not rendered then, and the gameweek on a
  // still-loading client is a guess rather than the one the server is on.
  const gameweekStats = useGameweekStats(
    currentGameweek,
    seasonVersion,
    !isLoading && gameweek.isDeadlinePassed
  )
  // Fetched on tap, not on profile open: most visits never open the panel.
  const otherPlayerGameweekDetail = useOtherPlayerGameweekDetail(
    history.viewingHistoryOwner?.userId,
    history.viewingHistoryGameweek
  )

  // The once-per-release "what changed" modal. Blocked while anything else is on screen — a
  // courtesy notice never lands on top of a fixture sheet, a results reveal or a league form.
  const updateNotice = useUpdateNotice({
    enabled: !isLoading && isSignedIn === true,
    isBlocked:
      predictions.editingGameId !== null ||
      history.viewingHistoryGameweek !== null ||
      topScorer.open !== null ||
      leagues.isCreatingLeague ||
      leagues.isJoiningLeague ||
      installPrompt.sheet !== null,
  })

  const appliedPhase = useAppliedPhase()
  useGameweekPhasePoll({
    isSimulating: simulation.isSimulating,
    ready: !isLoading && isSignedIn === true,
    appliedPhase,
    gameweek,
    predictions,
    season,
    leagues,
    onResultsRefreshed: bumpSeasonVersion,
  })
  useSessionBootstrap({
    startingGameweek: STARTING_GAMEWEEK,
    appliedPhase,
    session: { setIsLoading, setLoadError, setIsSignedIn },
    identity,
    gameweek,
    predictions,
    chips,
    season,
    leagues,
    rivals,
  })

  if (isLoading) {
    return (
      <div className="app">
        <p className="app__status-message">Loading…</p>
      </div>
    )
  }

  if (isSignedIn === false) {
    return <LoginScreen />
  }

  if (loadError) {
    // An explicit retry, not "please refresh": the most likely cause is a
    // fixtures fetch that failed, and the alternative — rendering an empty season — looks like a
    // working app with no football in it.
    return (
      <div className="app">
        <p className="app__status-message">{loadError}</p>
        <button
          type="button"
          className="app__status-retry"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    )
  }

  const view: AppView = {
    gameweek,
    predictions,
    chips,
    quests,
    revealSeen,
    hasStartedGw,
    leagues,
    leaguesRefresh,
    rivals,
    viewedPlayer,
    topScorer,
    simulation,
    devTools,
    scores,
    theme,
    identity,
    installPrompt,
    updateNotice,
    seasonHistory: season.seasonHistory,
    seasonDetails: season.seasonDetails,
    resultsVisible,

    live,
    forecasts,
    leagueNav,
    exploreLeagues,
    joinRequests,
    history,
    otherPlayerGameweekDetail,
    otherPlayerRevealGameweek,
    gameweekStats,
    toastMessage,
    setToastMessage,
  }

  // Rules navigation is app-wide (six entry points across four screens), so its
  // controller wraps the whole route switch rather than being threaded through it.
  return (
    <RulesProvider>
      <AppRoutes view={view} />
      {/* Above the route switch, not inside the tab shell: a release note is owed to whoever opens
          the app, including someone who lands on a league or a profile. */}
      <UpdateNoticeModal controller={updateNotice} />
    </RulesProvider>
  )
}

export default App
